import { ActionIcon, Button, Checkbox, Divider, Grid, Group, Loader, LoadingOverlay, Stack, Text, Textarea, Title } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

import { MultiLangCodes, NumberInput, Select, TextInput } from '@components';
import { EDIT_GOOD_LOCATION_SUBMIT_DELAY } from '@constants';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { EditGoodLocationProps, locationTypes, qualifierTypes } from './goodsLocationTypes';
import { useEditGoodsLocationFormInitializer } from './useEditGoodsLocationFormInitializer';

export const EditGoodLocation: React.FC<EditGoodLocationProps> = ({ goodLocationToEdit, onHide }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const { data: declarant, isLoading } = useDeclarant();
  const codelistSystems = useCodeListSystem({});

  const { onSubmit, form } = useEditGoodsLocationFormInitializer({
    declarant,
    goodLocationToEdit,
    onHide,
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      await onSubmit(values);
      setTimeout(() => {
        setIsSubmitting(false);
        onHide();
      }, EDIT_GOOD_LOCATION_SUBMIT_DELAY);
    } catch (error: any) {
      setIsSubmitting(false);
      setFormError(error.message || 'Failed to save the location of goods');
    }
  };

  const handleQualifierOfIdentificationChange = (value) => {
    form.setFieldValue('qualifierOfIdentification', value);
    form.clearErrors();
    form.validate();
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" my="xl">
        <Checkbox {...form.getInputProps('defaultLocation')} label="Default location" checked={form.values.defaultLocation} />
        <Select {...form.getInputProps('type')} label="Type of location" data={locationTypes} searchable />
        <Select
          {...form.getInputProps('qualifierOfIdentification')}
          label="Qualifier of identification"
          data={qualifierTypes}
          searchable
          onChange={handleQualifierOfIdentificationChange}
        />
      </Stack>

      <Divider mt="xl" />

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          UNLOCODE/Warehouse ID
        </Title>
        <TextInput {...form.getInputProps('unLocode')} label="Unlocode" />
        <Text fw={500} size="sm">
          UNLOCODE Description
        </Text>
        <Textarea {...form.getInputProps('unLocodeDescription')} minRows={7} autosize maxRows={7} />
      </Stack>

      <Divider mt="xl" />

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Customs office
        </Title>
        <MultiLangCodes {...form.getInputProps('customsOffice.referenceNumber')} label="Reference number" code="CL141" system={codelistSystems} />
      </Stack>

      <Divider mt="xl" />

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Authorisation number
        </Title>
        <TextInput {...form.getInputProps('authorisationNumber')} label="Authorisation number" />
      </Stack>

      <Divider mt="xl" />

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Additional identifier
        </Title>
        <TextInput {...form.getInputProps('additionalIdentifier')} label="Additional identifier" />
      </Stack>

      <Divider mt="xl" />

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          GNSS
        </Title>
        <Grid>
          <Grid.Col span={6}>
            <NumberInput {...form.getInputProps('gnss.latitude')} label="Latitude" />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput {...form.getInputProps('gnss.longitude')} label="Longitude" />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Economic operator
        </Title>
        <TextInput {...form.getInputProps('economicOperator.identificationNumber')} label="Identification number" />
      </Stack>

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Address
        </Title>
        <Grid>
          <Grid.Col span={8}>
            <TextInput {...form.getInputProps('address.street')} label="Address" />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput {...form.getInputProps('address.postcode')} label="Postcode" />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <TextInput {...form.getInputProps('address.city')} label="City" />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput {...form.getInputProps('address.country')} label="Country" />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Postcode
        </Title>
        <Grid>
          <Grid.Col span={4}>
            <TextInput {...form.getInputProps('postcode.postcode')} label="Postcode" />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput {...form.getInputProps('postcode.houseNumber')} label="House No." />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput {...form.getInputProps('postcode.country')} label="Country" />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="md" my="md">
        <Title order={5} fw={600}>
          Bonded Warehouses
        </Title>

        {form.values.warehouses.map((_, index) => (
          <Grid key={index} align="center">
            <Grid.Col span={5}>
              <MultiLangCodes {...form.getInputProps(`warehouses.${index}.type`)} label="Type" code="CL099" system={codelistSystems} />
            </Grid.Col>
            <Grid.Col span={5}>
              <TextInput {...form.getInputProps(`warehouses.${index}.identifier`)} label="Identifier" />
            </Grid.Col>
            <Grid.Col span={2}>
              <ActionIcon variant="subtle" size="lg" onClick={() => form.removeListItem('warehouses', index)}>
                <IconTrash size={18} />
              </ActionIcon>
            </Grid.Col>
          </Grid>
        ))}

        <Button variant="outline" onClick={() => form.insertListItem('warehouses', { type: '', identifier: '' })}>
          Add Warehouse
        </Button>
      </Stack>

      {formError && (
        <Text c="red" fw="bold">
          {formError}
        </Text>
      )}

      <Group justify="center" mt="xl">
        <Button type="button" key="cancel-modal-btn" onClick={() => onHide()} disabled={isSubmitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button
          key="save-modal-btn"
          disabled={!form.isValid() || isSubmitting}
          type="submit"
          leftSection={isSubmitting && <Loader color="#FFFFFF" />}
          variant="filled"
          size="lg"
        >
          Save
        </Button>
      </Group>
    </form>
  );
};
